import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  createBlogArticle,
  fetchBlogArticle,
  updateBlogArticle,
} from './useApi';

type FormMode = 'create' | 'update';

interface ArticleFormState {
  blogArticleID: string;
  title: string;
  author: string;
  summary: string;
  content: string;
  isPublished: boolean;
}

const defaultFormState: ArticleFormState = {
  blogArticleID: '',
  title: '',
  author: '',
  summary: '',
  content: '',
  isPublished: true,
};

const ArticlesManagement = () => {
  const [passwordInput, setPasswordInput] = useState('');
  const [adminPassword, setAdminPassword] = useState('');

  const [formMode, setFormMode] = useState<FormMode>('create');
  const [formState, setFormState] = useState<ArticleFormState>(defaultFormState);

  const [loadingArticle, setLoadingArticle] = useState(false);
  const [saving, setSaving] = useState(false);

  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  const applyEditPassword = () => {
    const trimmedPassword = passwordInput.trim();
    if (!trimmedPassword) {
      setSaveError('Edit pass is required for create and update actions.');
      return;
    }

    setSaveError(null);
    setSaveSuccess('Edit pass applied.');
    setAdminPassword(trimmedPassword);
  };

  const startCreate = () => {
    setFormMode('create');
    setFormState(defaultFormState);
    setSaveError(null);
    setSaveSuccess(null);
  };

  const updateForm = (key: keyof ArticleFormState, value: string | boolean) => {
    setFormState((current) => ({ ...current, [key]: value }));
  };

  const loadArticleForUpdate = async () => {
    const articleId = formState.blogArticleID.trim();
    if (!articleId) {
      setSaveError('Article ID is required to load article data.');
      return;
    }

    setLoadingArticle(true);
    setSaveError(null);
    setSaveSuccess(null);

    try {
      const article = await fetchBlogArticle(articleId);
      setFormState({
        blogArticleID: (article as any).blogArticleID ?? (article as any).blogArticleId ?? articleId,
        title: article.title || '',
        author: article.author || '',
        summary: article.summary || '',
        content: article.content || '',
        isPublished: !!article.isPublished,
      });
      setSaveSuccess('Article loaded.');
    } catch (error: any) {
      setSaveError(error.message || 'Failed to load article.');
    } finally {
      setLoadingArticle(false);
    }
  };

  const validateForm = (): string | null => {
    if (!formState.title.trim()) return 'Title is required.';
    if (!formState.summary.trim()) return 'Summary is required.';
    if (!formState.content.trim()) return 'Content (Markdown) is required.';
    if (formMode === 'update' && !formState.blogArticleID.trim()) return 'Article ID is required for update.';
    if (!adminPassword.trim()) return 'Apply Edit pass before saving.';
    return null;
  };

  const handleSaveArticle = async () => {
    setSaveError(null);
    setSaveSuccess(null);

    const validationError = validateForm();
    if (validationError) {
      setSaveError(validationError);
      return;
    }

    setSaving(true);

    try {
      if (formMode === 'create') {
        const created = await createBlogArticle(
          {
            title: formState.title.trim(),
            author: formState.author.trim(),
            summary: formState.summary.trim(),
            content: formState.content,
            isPublished: formState.isPublished,
          },
          adminPassword
        );

        setFormMode('update');
        setFormState({
          blogArticleID: (created as any).blogArticleID ?? (created as any).blogArticleId ?? '',
          title: created.title || '',
          author: created.author || '',
          summary: created.summary || '',
          content: created.content || '',
          isPublished: !!created.isPublished,
        });
        setSaveSuccess('Article created successfully.');
      } else {
        await updateBlogArticle(
          {
            blogArticleID: formState.blogArticleID.trim(),
            title: formState.title.trim(),
            author: formState.author.trim(),
            summary: formState.summary.trim(),
            content: formState.content,
            isPublished: formState.isPublished,
          },
          adminPassword
        );

        setSaveSuccess('Article updated successfully.');
      }
    } catch (error: any) {
      setSaveError(error.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className="f4k-page articles-management-page">
      <Box className="f4k-panel articles-management-panel" sx={{ width: 'min(1200px, 100%)' }}>
        <Typography variant="h4" className="f4k-title" sx={{ mb: 2 }}>
          Articles Management
        </Typography>

        <Typography variant="body2" sx={{ color: 'var(--f4k-text-600)', textAlign: 'left', mb: 2 }}>
          Content is stored as plain Markdown in the database and rendered to HTML in the browser.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <TextField
            label="Edit pass"
            type="password"
            value={passwordInput}
            onChange={(event) => setPasswordInput(event.target.value)}
            size="small"
          />
          <Button variant="contained" onClick={applyEditPassword}>
            Apply Edit Pass
          </Button>
          <Button variant="outlined" onClick={startCreate}>
            New Article
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Button
            variant={formMode === 'create' ? 'contained' : 'outlined'}
            onClick={() => setFormMode('create')}
          >
            Create
          </Button>
          <Button
            variant={formMode === 'update' ? 'contained' : 'outlined'}
            onClick={() => setFormMode('update')}
          >
            Update
          </Button>
        </Box>

        <Stack spacing={1.5}>
          {formMode === 'update' && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                label="Article ID"
                value={formState.blogArticleID}
                onChange={(event) => updateForm('blogArticleID', event.target.value)}
                fullWidth
                size="small"
              />
              <Button variant="outlined" onClick={loadArticleForUpdate} disabled={loadingArticle}>
                {loadingArticle ? 'Loading...' : 'Load Article'}
              </Button>
            </Box>
          )}

          <TextField
            label="Title"
            value={formState.title}
            onChange={(event) => updateForm('title', event.target.value)}
            fullWidth
            size="small"
          />

          <TextField
            label="Author"
            value={formState.author}
            onChange={(event) => updateForm('author', event.target.value)}
            fullWidth
            size="small"
          />

          <TextField
            label="Summary"
            value={formState.summary}
            onChange={(event) => updateForm('summary', event.target.value)}
            fullWidth
            multiline
            minRows={2}
          />

          <TextField
            label="Content (Markdown)"
            value={formState.content}
            onChange={(event) => updateForm('content', event.target.value)}
            fullWidth
            multiline
            minRows={10}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
            <Switch
              checked={formState.isPublished}
              onChange={(_, checked) => updateForm('isPublished', checked)}
            />
            <Typography>Published</Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Button variant="contained" onClick={handleSaveArticle} disabled={saving}>
              {saving ? 'Saving...' : formMode === 'create' ? 'Create Article' : 'Update Article'}
            </Button>
          </Box>

          {saveError && <Alert severity="error">{saveError}</Alert>}
          {saveSuccess && <Alert severity="success">{saveSuccess}</Alert>}
        </Stack>
      </Box>
    </Box>
  );
};

export default ArticlesManagement;
